import * as THREE from 'three';
import { ATMO } from '../config/tuning';
import { MISSILE, WARHEAD, type MissileParams } from '../config/weapons';
import type { Checksum, Checksummable } from '../core/Checksum';
import { Rng } from '../core/Rng';
import type { Atmosphere } from '../flight/Atmosphere';
import {
  addGravityBias,
  closestApproachM,
  closingSpeedMs,
  lateralComponent,
  limitAcceleration,
  proportionalNavigation,
  timeToClosestApproachS,
} from './Guidance';
import { LockState, Seeker, type SeekerContext } from './Seeker';
import type { RadarTarget } from './Detection';
import type { Terrain } from '../world/Terrain';

/**
 * A missile: a physical object with an energy state, not a homing token (SPEC §3.6.1).
 *
 * This is non-negotiable and it is the whole reason the game is playable. The evasion skill
 * depends entirely on the missile *running out of energy*, so everything here that could have
 * been faked is instead integrated: thrust by phase, drag that rises through the transonic
 * region, and — the term that matters most — an induced drag penalty proportional to the square
 * of the g the missile is being asked to pull. Every degree of turn the player forces costs the
 * missile speed it cannot get back, and a missile below `minSpeedMs` cannot intercept anything.
 *
 * Phases (SPEC §3.6.1): launch → boost → sustain → coast → terminal → expiry.
 *
 * Units: metres, m/s, m/s², newtons, kilograms, seconds.
 */

export const MissilePhase = {
  Launch: 'launch',
  Boost: 'boost',
  Sustain: 'sustain',
  Coast: 'coast',
  Terminal: 'terminal',
  Spent: 'spent',
} as const;
export type MissilePhaseId = (typeof MissilePhase)[keyof typeof MissilePhase];

export const MissileOutcome = {
  /** Still flying. */
  Flying: 'flying',
  /** Proximity fuse fired. `missDistanceM` is how close it got. */
  Detonated: 'detonated',
  /** Timed out, ran out of speed, or hit the ground: a visible self-destruct. */
  Expired: 'expired',
} as const;
export type MissileOutcomeId = (typeof MissileOutcome)[keyof typeof MissileOutcome];

// Module-scope scratch — a salvo of missiles ticking at 120 Hz must not allocate (SPEC §4.3).
const relativePosition = new THREE.Vector3();
const relativeVelocity = new THREE.Vector3();
const command = new THREE.Vector3();
const lateral = new THREE.Vector3();
const acceleration = new THREE.Vector3();
const previousRelative = new THREE.Vector3();
const dragScratch = new THREE.Vector3();
const fuseFraction = { value: 0 };

/**
 * Drag coefficient multiplier through the transonic region, unitless.
 *
 * Subsonic missiles are cheap to push; the wave drag rise through Mach 1 is what makes a boosted
 * round decelerate hard once the motor quits, and it is why a missile launched at long range
 * arrives slow. Peaks just above Mach 1 and settles to a supersonic plateau.
 */
export function machDragFactor(mach: number): number {
  const peak = MISSILE.machDragPeak;
  const plateau = MISSILE.machDragSupersonic;
  const width = MISSILE.machDragWidth;
  const offset = (mach - MISSILE.machDragPeakMach) / width;
  const bump = (peak - 1) * Math.exp(-offset * offset);
  // Below the peak the baseline is 1; above it, the plateau.
  const baseline = mach <= MISSILE.machDragPeakMach ? 1 : plateau;
  return Math.max(baseline, 1 + bump);
}

/** Speed of sound at an altitude, m/s. Falls with temperature up to the tropopause. */
export function speedOfSoundMs(altitudeM: number): number {
  const temperatureK = 288.15 - 0.0065 * Math.min(Math.max(altitudeM, 0), 11000);
  return 20.05 * Math.sqrt(temperatureK);
}

/**
 * Damage from a detonation (SPEC §3.6.4): `base · (1 − d/r)^1.6 · aspect`.
 *
 * Returns the fraction of airframe integrity removed. Graded, not binary, because SPEC §3.6.4 is
 * explicit that a wounded drone limping home is the most valuable outcome the game can produce —
 * so most detonations should hurt rather than kill.
 */
export function detonationDamage(
  missDistanceM: number,
  fuseRadiusM: number,
  warheadYield: number,
  aspectFactor: number,
): number {
  if (missDistanceM >= fuseRadiusM) return 0;
  const falloff = (1 - missDistanceM / fuseRadiusM) ** 1.6;
  return WARHEAD.baseDamage * warheadYield * falloff * aspectFactor;
}

/**
 * How squarely the warhead's fragmentation pattern catches the airframe, unitless.
 * A round arriving from the beam presents the whole planform; one arriving down the axis does not.
 */
export function damageAspectFactor(
  missileVelocityMs: THREE.Vector3,
  targetForward: THREE.Vector3,
): number {
  const speed = missileVelocityMs.length();
  if (speed < 1e-6) return WARHEAD.aspectMax;
  const alignment = Math.abs(missileVelocityMs.dot(targetForward) / speed);
  return WARHEAD.aspectMax + (WARHEAD.aspectMin - WARHEAD.aspectMax) * alignment;
}

/**
 * Placeholder parameters for a missile that has never been launched. A pooled round holds a
 * seeker from the moment it is created, and a seeker needs something to read — this is what it
 * reads until `launch` binds the real thing.
 */
const MISSILE_PLACEHOLDER: MissileParams = {
  massKg: 1,
  diameterM: 0.1,
  boostThrustN: 0,
  boostDurationS: 0,
  sustainThrustN: 0,
  sustainDurationS: 0,
  cd0: 0.4,
  inducedDragK: 0,
  maxG: 1,
  navConstant: 3,
  seeker: 'radar-command',
  seekerFovDeg: 30,
  seekerLockRangeM: 0,
  maxFlightTimeS: 0,
  minSpeedMs: 0,
  fuseRadiusM: 0,
  warheadYield: 0,
  chaffSusceptibility: 0,
  flareSusceptibility: 0,
};

export class Missile implements Checksummable {
  id = -1;
  /** Which site fired it, for the debrief and for command guidance. */
  siteId = -1;
  designation = '';
  active = false;

  readonly positionM = new THREE.Vector3();
  readonly velocityMs = new THREE.Vector3();
  /** Previous tick's position, for render interpolation (SPEC §4.3). */
  readonly previousPositionM = new THREE.Vector3();

  phase: MissilePhaseId = MissilePhase.Launch;
  ageS = 0;
  massKg = 0;

  /** Set on the tick the missile stops flying. */
  outcome: MissileOutcomeId = MissileOutcome.Flying;
  missDistanceM = Number.POSITIVE_INFINITY;
  /** Where the fuse fired, for the effects system. */
  readonly detonationM = new THREE.Vector3();

  /** Lateral acceleration actually achieved this tick, m/s² — what induced drag is charged on. */
  commandedLateralMs2 = 0;
  /**
   * Acceleration the fins are currently producing, m/s². Lags the guidance command by
   * `MISSILE.autopilotLagS` — see the note there; this is the state that lag needs.
   */
  private readonly achievedMs2 = new THREE.Vector3();
  /** Seconds to closest approach on the current geometry; drives the terminal phase and the HUD. */
  timeToGoS = Number.POSITIVE_INFINITY;
  closureMs = 0;

  /** Defaults are placeholders; `launch` rebinds them. Both objects are reused, never re-made. */
  params: MissileParams = MISSILE_PLACEHOLDER;
  readonly seeker = new Seeker(MISSILE_PLACEHOLDER);
  private readonly rng = new Rng(1);
  private crossSectionM2 = 0;

  /** Prepare a pooled missile for flight. */
  launch(
    id: number,
    siteId: number,
    designation: string,
    params: MissileParams,
    originM: THREE.Vector3,
    towardM: THREE.Vector3,
    seed: number,
  ): void {
    this.id = id;
    this.siteId = siteId;
    this.designation = designation;
    this.params = params;
    this.seeker.configure(params);
    this.rng.restore(seed);
    this.active = true;

    this.positionM.copy(originM);
    this.previousPositionM.copy(originM);

    // Ejection: canted up off the rail, no guidance yet (SPEC §3.6.1).
    this.velocityMs.copy(towardM).sub(originM);
    if (this.velocityMs.lengthSq() < 1e-9) this.velocityMs.set(0, 1, 0);
    this.velocityMs.normalize();
    const cant = (MISSILE.ejectionCantDeg * Math.PI) / 180;
    this.velocityMs.y += Math.tan(cant);
    this.velocityMs.normalize().multiplyScalar(MISSILE.ejectionSpeedMs);

    this.phase = MissilePhase.Launch;
    this.ageS = 0;
    this.massKg = params.massKg;
    this.outcome = MissileOutcome.Flying;
    this.missDistanceM = Number.POSITIVE_INFINITY;
    this.commandedLateralMs2 = 0;
    this.achievedMs2.set(0, 0, 0);
    this.timeToGoS = Number.POSITIVE_INFINITY;
    this.closureMs = 0;
    this.crossSectionM2 = Math.PI * (params.diameterM * 0.5) ** 2;
    this.seeker.lastKnownM.copy(towardM);
  }

  /** Speed, m/s. Below `params.minSpeedMs` the missile can no longer intercept and self-destructs. */
  get speedMs(): number {
    return this.velocityMs.length();
  }

  /**
   * Advance one tick. Returns the outcome; anything but `Flying` retires the missile.
   *
   * Order matters: seeker first (it decides what we are guiding on), then guidance, then the
   * forces the guidance implies, then integration, and only then the fuse — which is evaluated
   * across the step just taken rather than at its end.
   */
  tick(
    dtS: number,
    context: SeekerContext,
    atmosphere: Atmosphere,
    siteHasTrack: boolean,
  ): MissileOutcomeId {
    const target = context.target;
    this.previousPositionM.copy(this.positionM);
    this.ageS += dtS;

    // --- Geometry the rest of the tick reads from.
    relativePosition.copy(target.positionM).sub(this.positionM);
    relativeVelocity.copy(target.velocityMs).sub(this.velocityMs);
    this.closureMs = closingSpeedMs(relativePosition, relativeVelocity);
    this.timeToGoS = timeToClosestApproachS(relativePosition, relativeVelocity);
    previousRelative.copy(relativePosition);

    this.updatePhase();

    // --- Seeker, then guidance.
    if (this.phase !== MissilePhase.Launch) {
      // Held by reference: the context is the caller's, reused across every missile in the air.
      context.missilePositionM = this.positionM;
      context.missileVelocityMs = this.velocityMs;
      context.siteHasTrack = siteHasTrack;
      context.terminal = this.phase === MissilePhase.Terminal;
      this.seeker.update(dtS, context, this.rng, MISSILE.lockMemoryS);
    }

    acceleration.set(0, -ATMO.G, 0);
    this.commandedLateralMs2 = 0;

    if (this.phase !== MissilePhase.Launch && this.seeker.state !== LockState.Ballistic) {
      // Guide on whatever the seeker is looking at — the target, or a decoy that fooled it.
      relativePosition.copy(this.seeker.aimPointM).sub(this.positionM);
      relativeVelocity.copy(this.seeker.aimVelocityMs).sub(this.velocityMs);

      const gain =
        this.params.navConstant *
        (this.phase === MissilePhase.Terminal ? MISSILE.terminalGainScale : 1);
      proportionalNavigation(relativePosition, relativeVelocity, this.velocityMs, gain, command);
      addGravityBias(command);
      limitAcceleration(command, this.params.maxG);

      // The fins take time to build the commanded g. First-order lag, and the single term that
      // makes a late break survivable and an early one useless (see MISSILE.autopilotLagS).
      const response = 1 - Math.exp(-dtS / MISSILE.autopilotLagS);
      this.achievedMs2.lerp(command, response);

      // Only the part across the flight path bends the trajectory and costs induced drag, and it
      // is charged on what the airframe actually pulled, not on what was asked for.
      this.commandedLateralMs2 = lateralComponent(this.achievedMs2, this.velocityMs, lateral);
      acceleration.add(this.achievedMs2);
    }

    // --- Thrust and drag.
    const thrustN = this.thrustN();
    const speed = this.velocityMs.length();
    if (thrustN > 0 && speed > 1e-6) {
      acceleration.addScaledVector(this.velocityMs, thrustN / (this.massKg * speed));
    }
    this.addDragTo(acceleration, atmosphere, speed);

    // --- Semi-implicit Euler, as everywhere else in this project (SPEC §4.4).
    this.velocityMs.addScaledVector(acceleration, dtS);
    this.positionM.addScaledVector(this.velocityMs, dtS);

    return this.resolveEnd(dtS, target, context.terrain);
  }

  checksum(into: Checksum): void {
    into.addFloat(this.positionM.x);
    into.addFloat(this.positionM.y);
    into.addFloat(this.positionM.z);
    into.addFloat(this.velocityMs.x);
    into.addFloat(this.velocityMs.y);
    into.addFloat(this.velocityMs.z);
    into.addFloat(this.ageS);
    into.addInt(this.seeker.state === LockState.Locked ? 1 : 0);
  }

  // --- Internals ---------------------------------------------------------------------------

  private updatePhase(): void {
    if (this.phase === MissilePhase.Spent) return;

    const boostEndS = MISSILE.launchPhaseS + this.params.boostDurationS;
    const sustainEndS = boostEndS + this.params.sustainDurationS;

    // Terminal overrides the motor phases: the fuse arms and the gain rises on time-to-go alone.
    if (
      this.timeToGoS >= 0 &&
      this.timeToGoS <= MISSILE.terminalTimeS &&
      this.ageS > MISSILE.launchPhaseS
    ) {
      this.phase = MissilePhase.Terminal;
      return;
    }

    if (this.ageS <= MISSILE.launchPhaseS) this.phase = MissilePhase.Launch;
    else if (this.ageS <= boostEndS) this.phase = MissilePhase.Boost;
    else if (this.ageS <= sustainEndS) this.phase = MissilePhase.Sustain;
    else this.phase = MissilePhase.Coast;
  }

  private thrustN(): number {
    const boostEndS = MISSILE.launchPhaseS + this.params.boostDurationS;
    const sustainEndS = boostEndS + this.params.sustainDurationS;
    if (this.ageS <= MISSILE.launchPhaseS) return 0;
    if (this.ageS <= boostEndS) return this.params.boostThrustN;
    if (this.ageS <= sustainEndS) return this.params.sustainThrustN;
    return 0;
  }

  /**
   * `D = ½·ρ·V²·C_D·A`, with the transonic rise and the induced penalty.
   *
   * The induced term is the mechanism the whole evasion model rests on: `C_D` gains
   * `inducedDragK · g²` where g is the lateral acceleration the guidance just commanded. A
   * missile flying a clean constant-bearing intercept commands nothing and pays nothing; one
   * being hauled around by a late break pays four times its base drag and arrives — if it
   * arrives — too slow to correct.
   */
  private addDragTo(
    accelerationMs2: THREE.Vector3,
    atmosphere: Atmosphere,
    speedMs: number,
  ): void {
    if (speedMs < 1e-6) return;

    const densityKgM3 = atmosphere.densityAtM(this.positionM.y);
    const mach = speedMs / speedOfSoundMs(this.positionM.y);
    const commandedG = this.commandedLateralMs2 / ATMO.G;
    const dragCoefficient =
      this.params.cd0 * machDragFactor(mach) + this.params.inducedDragK * commandedG * commandedG;

    const dragN = 0.5 * densityKgM3 * speedMs * speedMs * dragCoefficient * this.crossSectionM2;
    dragScratch.copy(this.velocityMs).multiplyScalar(-dragN / (this.massKg * speedMs));
    accelerationMs2.add(dragScratch);
  }

  /**
   * Fuse, expiry and ground impact.
   *
   * The fuse is evaluated across the step just taken, not at its end: at 800 m/s of closure a
   * 120 Hz tick covers 6.7 m against a 12 m fuse radius, so an endpoint test would step straight
   * through the envelope (SPEC §4.4, and the M6 implementation notes).
   */
  private resolveEnd(dtS: number, target: RadarTarget, terrain: Terrain): MissileOutcomeId {
    // Relative motion across the step. previousRelative was captured before integration.
    relativeVelocity.copy(target.velocityMs).sub(this.velocityMs);
    const missM = closestApproachM(previousRelative, relativeVelocity, dtS, fuseFraction);

    if (this.ageS >= MISSILE.fuseArmS && missM <= this.params.fuseRadiusM) {
      this.missDistanceM = missM;
      this.detonationM
        .copy(this.previousPositionM)
        .lerp(this.positionM, fuseFraction.value);
      this.outcome = MissileOutcome.Detonated;
      this.phase = MissilePhase.Spent;
      return this.outcome;
    }

    const belowGround = this.positionM.y <= terrain.surfaceHeightAt(this.positionM.x, this.positionM.z);
    // Out of energy only counts once the motor has quit. A round still under boost is slow
    // because it has just left the rail, not because it has been beaten — checking before then
    // self-destructs every missile a second after launch, which is a very quiet way to make the
    // whole threat model do nothing.
    const motorRunning = this.thrustN() > 0 || this.ageS <= MISSILE.launchPhaseS;
    const tooSlow = !motorRunning && this.speedMs < this.params.minSpeedMs;
    const timedOut = this.ageS >= this.params.maxFlightTimeS;

    if (belowGround || tooSlow || timedOut) {
      this.missDistanceM = missM;
      this.detonationM.copy(this.positionM);
      this.outcome = MissileOutcome.Expired;
      this.phase = MissilePhase.Spent;
      return this.outcome;
    }

    return MissileOutcome.Flying;
  }
}
